import React from 'react';
import { Link } from 'react-router-dom';
import { styled, Box } from '@mui/material';
import * as FolderStore from '../store/Folder';
import * as ImageStore from '../store/Image';
import { useSelector } from 'react-redux';
import { RouteContext } from '../App';
import { Folder, Image } from '../models';

const FolderPath = styled('ul')({
    padding: 0,
    paddingLeft: 12,
    margin: 0,
    '& li': {
        listStyle: 'none',
        display: 'inline-block'
    }
});

const PathDivider = styled('div')(({ theme }) => ({
    color: '#FFF',
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
}));

const StyledLink = styled(Link)({
    '&:link': {
        textDecoration: 'none',
        color: '#CCC'
    },
    '&:hover': {
        textDecoration: 'underline'
    },
    '&:visited': {
        color: 'inherit'
    }
});

const Breadcrumbs = (props: { folder?: Folder, image?: Image }) => {
    const { folder, image } = props;
    const allFolders = useSelector(FolderStore.selectors.folders);
    const currentFolderId = folder ? folder.id : (image ? image.folderId : '');

    if (!folder && !image) {
        return null; // nothing appears to be selected
    }

    const currentPath = FolderStore.utils.getPath(allFolders, currentFolderId);
    const currentPathIds = FolderStore.utils.getPathIds(allFolders, currentFolderId);

    if (!currentPath) {
        return null;
    }

    const renderCrumb = (name: string, url: string) => {
        return (
            <Box sx={{ typography: 'body1', display: 'inline-block', textTransform: 'none' }}>
                <li>
                    <StyledLink to={url}>{name}</StyledLink>
                </li>
            </Box>
        );
    }

    const renderCrumbDivider = () => {
        return (
            <Box sx={{ typography: 'body1', display: 'inline-block', textTransform: 'none' }}>
                <PathDivider>&gt;</PathDivider>
            </Box>
        );
    }

    return (
        <FolderPath>
            {currentPath.path.map((item, i) => {
                const pathId = currentPathIds && currentPathIds.path ? currentPathIds.path[i] : '';
                const hasNext = i + 1 < currentPath.path.length;
                return (
                    <div key={pathId} style={{ display: 'inline-block' }}>
                        {renderCrumb(item, `/folder/${pathId}`)}
                        {hasNext || image ? renderCrumbDivider() : null}
                    </div>
                );
            })}
            {image ? renderCrumb(image.fileName, `/image/${image.id}`) : null}
        </FolderPath>
    );
}

const BreadcrumbsProvider = () => {
    const images = useSelector(ImageStore.selectors.imagesFlattened);
    const folders = useSelector(FolderStore.selectors.foldersFlattened);

    return (
        <RouteContext.Consumer>
            {(ctx) => {
                const folder = folders.find(f => f.id.toLowerCase() === ctx.id?.toLowerCase());
                const image = images.find(i => i.id.toLowerCase() === ctx.id?.toLowerCase());
                return <Breadcrumbs folder={folder} image={image} />;
            }}
        </RouteContext.Consumer>
    )
}

export default BreadcrumbsProvider;